const STAR_PATH =
  'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

const SHIELD_PATH =
  'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z';

const REPLY_ARROW_PATH =
  'M9 18l-6-6 6-6v4h6c3.31 0 6 2.69 6 6v6h-2v-6c0-2.21-1.79-4-4-4H9v4z';

const USER_ICON_PATH =
  'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z';

// Reviews imported without a body were given this placeholder so they'd import;
// treat it as "no text" and hide the body block (stars/title still show).
const PLACEHOLDER_BODY = '[no text]';

const POLL_INTERVAL_MS = 150;
const POLL_TIMEOUT_MS = 15000;

const lang = document.documentElement.lang || 'en';
const numberFormatter = new Intl.NumberFormat(lang);
const dateFormatter = new Intl.DateTimeFormat(lang, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return dateFormatter.format(d);
}

function pluralize(count, one, other) {
  return (count === 1 ? one : other).replace('{count}', numberFormatter.format(count));
}

function fractionalStars(rating, sizeClass) {
  let html = '';
  for (let i = 1; i <= 5; i++) {
    const fillPct = Math.max(0, Math.min(1, rating - (i - 1))) * 100;
    html += `<span class="cr-star ${sizeClass}" aria-hidden="true"><svg class="cr-star__svg cr-star__svg--empty" viewBox="0 0 32 32"><path d="${STAR_PATH}"/></svg><svg class="cr-star__svg cr-star__svg--fill" viewBox="0 0 32 32" style="--fill-pct: ${fillPct}%"><path d="${STAR_PATH}"/></svg></span>`;
  }
  return html;
}

function getWidgetData(productId) {
  const reviewWidget = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget;
  if (!reviewWidget) return null;
  return reviewWidget[productId] || reviewWidget[String(productId)] || null;
}

function waitForWidgetData(productId) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const data = getWidgetData(productId);
      if (data) return resolve({ status: 'data', data });

      const elapsed = Date.now() - start;
      const judgemeReady = document.querySelector(
        '.jdgm-widget.jdgm-review-widget.jdgm--done-setup-widget'
      );
      // Judge.me's widget has finished setup but didn't inline data for
      // this product — that's the "0 reviews" case, not an error.
      if (judgemeReady && elapsed > 3000) return resolve({ status: 'empty' });

      if (elapsed > POLL_TIMEOUT_MS) return resolve({ status: 'error' });
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
  });
}

function findJudgemeWriteTrigger(host) {
  const local = host.querySelector('.jdgm-write-rev-link');
  if (local) return local;
  const selectors = [
    '.jdgm-write-rev-link',
    '.jm-review-widget-minimal-header__action-buttons .jm-button--primary',
    '.jm-action-buttons__button.jm-button--primary',
    '[data-jdgm-action="write-review"]',
  ];
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) return el;
  }
  return null;
}

function renderSummary(host, data, labels, classes) {
  const root = host.querySelector('[data-cr-summary]');
  if (!root) return;

  const avg = Number(data.average_rating || 0);
  const total = Number(
    data.number_of_reviews || data.total_reviews || (data.reviews ? data.reviews.length : 0) || 0
  );

  if (!total) {
    root.innerHTML = `<p class="customer-reviews__empty">${escapeHtml(labels.empty)}</p>`;
    return;
  }

  const histogram = Array.isArray(data.histogram) ? data.histogram : [];
  const byRating = new Map(histogram.map((h) => [Number(h.rating), h]));

  const ratingLine = `<div class="cr-rating-line"><span class="cr-rating-line__stars">${fractionalStars(avg, 'cr-star--md')}</span><span class="cr-rating-line__value ${escapeHtml(classes.rating || '')}">${escapeHtml(avg.toFixed(1))} ${escapeHtml(labels.out_of)}</span></div>`;
  const countLine = `<p class="cr-count">${escapeHtml(pluralize(total, labels.reviews_one, labels.reviews_other))}</p>`;
  // Each histogram row is a button: clicking it opens the all-reviews modal
  // pre-filtered to that star rating (wired in hydrate).
  const rows = [5, 4, 3, 2, 1]
    .map((rating) => {
      const bucket = byRating.get(rating) || { percentage: 0 };
      const pct = Math.round(Number(bucket.percentage || 0));
      const rowLabel = (labels.stars_row || '{count} stars').replace('{count}', rating);
      return `<button type="button" class="cr-histogram__row" data-cr-rating="${rating}" aria-haspopup="dialog" aria-label="${escapeHtml(rowLabel)}, ${pct}%"><span class="cr-histogram__label">${escapeHtml(rowLabel)}</span><span class="cr-histogram__bar"><span class="cr-histogram__bar-fill" style="--pct: ${pct}%"></span></span><span class="cr-histogram__pct">${pct}%</span></button>`;
    })
    .join('');

  root.innerHTML = `${ratingLine}${countLine}<div class="cr-histogram">${rows}</div>`;
}

function renderVerified(review, labels) {
  if (!review.verified_buyer) return '';
  return `<span class="cr-card__verified"><svg class="cr-card__verified-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="${SHIELD_PATH}"/></svg><span>${escapeHtml(labels.verified)}</span></span>`;
}

function renderAvatar(review, options) {
  if (review.avatar_image_url) {
    return `<div class="cr-card__avatar"><img src="${escapeHtml(review.avatar_image_url)}" alt="" loading="lazy"></div>`;
  }

  const fallback = options.avatar_fallback;

  if (fallback === 'hide') return '';

  if (fallback === 'initial') {
    const initial = (review.reviewer_initial || (review.reviewer_name || '').trim().charAt(0) || '').toUpperCase();
    if (initial) {
      return `<div class="cr-card__avatar cr-card__avatar--initial" aria-hidden="true">${escapeHtml(initial)}</div>`;
    }
  }

  // Default fallback: a generic user icon.
  return `<div class="cr-card__avatar cr-card__avatar--icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="${USER_ICON_PATH}"/></svg></div>`;
}

// Judge.me picture shapes differ between the inlined widget data and the CDN
// widget endpoint: `pictures_urls` can hold plain URL strings, size-keyed
// objects ({ compact, small, huge, original }), or objects nesting those under
// `urls`; the documented API uses `pictures` with { hidden, urls: {...} }.
// Rendering a size-keyed object straight into src stringifies it to
// "[object Object]" and every review photo paints as a broken image — normalize
// all the shapes to { thumb, full } instead.
const PICTURE_THUMB_KEYS = ['compact', 'small', 'huge', 'original'];
const PICTURE_FULL_KEYS = ['huge', 'original', 'compact', 'small'];

function pickPictureUrl(source, keys) {
  if (!source || typeof source !== 'object') return '';
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return '';
}

// Last resort for a shape whose size keys aren't named any of the above: take
// the first value that actually looks like a URL, so an unanticipated key name
// still paints a photo instead of silently rendering nothing.
function firstUrlValue(source) {
  if (!source || typeof source !== 'object') return '';
  for (const value of Object.values(source)) {
    if (typeof value === 'string' && /^(https?:)?\/\//.test(value)) return value;
  }
  return '';
}

function normalizePicture(picture) {
  if (typeof picture === 'string') return picture ? { thumb: picture, full: picture } : null;
  if (!picture || typeof picture !== 'object' || picture.hidden) return null;
  const urls = picture.urls && typeof picture.urls === 'object' ? picture.urls : picture;
  const thumb = pickPictureUrl(urls, PICTURE_THUMB_KEYS) || firstUrlValue(urls);
  const full = pickPictureUrl(urls, PICTURE_FULL_KEYS) || firstUrlValue(urls);
  if (!thumb && !full) return null;
  return { thumb: thumb || full, full: full || thumb };
}

function reviewPictures(review) {
  const raw = Array.isArray(review.pictures_urls)
    ? review.pictures_urls
    : Array.isArray(review.pictures)
      ? review.pictures
      : [];
  return raw.map(normalizePicture).filter(Boolean);
}

function renderPhotos(review, labels) {
  const pictures = reviewPictures(review);
  if (!pictures.length) return '';
  const zoomLabel = escapeHtml((labels && labels.zoom) || 'Zoom');
  return `<div class="cr-card__photos">${pictures
    .map(
      (picture) =>
        `<button type="button" class="cr-card__photo" data-cr-photo data-full-src="${escapeHtml(picture.full)}" aria-label="${zoomLabel}"><img src="${escapeHtml(picture.thumb)}" alt="" loading="lazy"></button>`
    )
    .join('')}</div>`;
}

// Review photos open in a lightbox instead of a new tab. One dialog per block,
// rendered as a sibling of the all-reviews modal, so this single wiring serves
// both the on-page preview feed and the modal feed (both live inside `host`).
// The dialog reuses base.css's `.dialog-modal` chrome — backdrop, open
// animation, full-screen mobile treatment — rather than inventing modal styling.
function wireLightbox(host, labels) {
  const dialog = host.querySelector('[data-cr-lightbox]');
  const image = dialog && dialog.querySelector('[data-cr-lightbox-image]');
  if (!dialog || !image) return;

  const closeButton = dialog.querySelector('[data-cr-lightbox-close]');
  let lockedByUs = false;

  // Every exit path — the close button, a backdrop click, and Escape's native
  // `cancel` — ends in the dialog's own `close` event, so the cleanup lives
  // here once and can't be skipped by a path that bypasses our handlers.
  dialog.addEventListener('close', () => {
    if (lockedByUs) {
      document.documentElement.removeAttribute('scroll-lock');
      lockedByUs = false;
    }
    image.removeAttribute('src');
  });

  const close = () => {
    if (dialog.open) dialog.close();
  };

  host.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-cr-photo]');
    if (!trigger || !host.contains(trigger)) return;

    const src = trigger.dataset.fullSrc;
    if (!src || dialog.open) return;

    image.src = src;
    image.alt = (labels && labels.zoom) || '';

    // Opened from inside the all-reviews modal the page is already locked by
    // that dialog. `html[scroll-lock]` is a plain `overflow: hidden` that never
    // touches the saved scroll offset, so it composes with that lock instead of
    // clobbering it — and we only clear what we set.
    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      lockedByUs = true;
    }

    dialog.showModal();
    window.publishStorefrontEvent?.('review_photo_opened', { placement: 'product_page' });
  });

  if (closeButton) closeButton.addEventListener('click', close);

  // Backdrop click. The dialog is a sibling of the reviews modal, so this
  // never reaches that modal's own outside-click handler.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });
}

function renderReply(review, replyShop) {
  // Judge.me's widget data exposes the reply as `reply_content` (HTML string);
  // older shapes nested it under `shop_reply`/`reply`. Support both.
  const legacy = review.shop_reply || review.reply;
  let body = '';
  if (typeof review.reply_content === 'string' && review.reply_content.trim()) {
    body = review.reply_content;
  } else if (legacy) {
    body = legacy.body_html || (legacy.body ? `<p>${escapeHtml(legacy.body)}</p>` : '');
  }
  if (!body) return '';
  const shopName = review.shop_reply_name || (legacy && legacy.shop_name) || replyShop.name || '';
  const logo = replyShop.logo
    ? `<span class="cr-card__reply-logo"><img src="${escapeHtml(replyShop.logo)}" alt="" loading="lazy"></span>`
    : `<span class="cr-card__reply-logo" aria-hidden="true"></span>`;
  return `<div class="cr-card__reply"><svg class="cr-card__reply-arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="${REPLY_ARROW_PATH}"/></svg><div><div class="cr-card__reply-author">${logo}<span>${escapeHtml(shopName)}</span></div><div class="cr-card__reply-body">${body}</div></div></div>`;
}

function isPlaceholderBody(review) {
  const raw = review.body_html || review.body || '';
  const text = String(raw).replace(/<[^>]*>/g, '').trim();
  return text.toLowerCase() === PLACEHOLDER_BODY;
}

function renderCard(review, labels, classes, replyShop, options) {
  const name = review.is_anonymous_reviewer ? labels.anonymous : review.reviewer_name || labels.anonymous;
  const title = review.title
    ? `<span class="cr-card__title ${escapeHtml(classes.title || '')}">${escapeHtml(review.title)}</span>`
    : '';
  const body = isPlaceholderBody(review)
    ? ''
    : review.body_html || (review.body ? `<p>${escapeHtml(review.body)}</p>` : '');
  return `
    <article class="cr-card">
      <div class="cr-card__head">
        <div class="cr-card__author">
          ${renderAvatar(review, options)}
          <div class="cr-card__author-text">
            <span class="cr-card__name ${escapeHtml(classes.name || '')}">${escapeHtml(name)}</span>
            <time class="cr-card__date" datetime="${escapeHtml(review.created_at || '')}">${escapeHtml(formatDate(review.created_at))}</time>
          </div>
        </div>
        ${renderVerified(review, labels)}
      </div>
      <div class="cr-card__rating-row">
        <span class="cr-card__stars" aria-label="${escapeHtml(review.rating)} out of 5">${fractionalStars(review.rating, 'cr-star--sm')}</span>
        ${title}
      </div>
      ${body ? `<div class="cr-card__body ${escapeHtml(classes.body || '')}">${body}</div>` : ''}
      ${renderPhotos(review, labels)}
      ${renderReply(review, replyShop)}
    </article>
  `;
}

// Judge.me only inlines the first page of reviews into jdgm.data; the rest are
// fetched from its public, token-less, CORS-open widget CDN endpoint (the same
// one the native Judge.me widget uses), which returns review objects in the
// exact shape we already render.
function buildReviewsUrl(shopDomain, productId, page, rating, sort) {
  const url = new URL('https://cdn.judge.me/reviews/reviews_for_widget');
  url.searchParams.set('product_id', productId);
  url.searchParams.set('page', String(page));
  url.searchParams.set('sort_by', (sort && sort.by) || 'created_at');
  url.searchParams.set('sort_dir', (sort && sort.dir) || 'desc');
  url.searchParams.set('skip_other_languages', 'true');
  url.searchParams.set('widget_theme', 'standard');
  url.searchParams.set('shop_domain', shopDomain);
  url.searchParams.set('platform', 'shopify');
  // Filter by star rating when the modal's rating filter is active. We also
  // filter the response client-side (in createReviewBrowser), so results stay
  // correct even if the server ignores this param.
  if (rating) url.searchParams.set('rating', String(rating));
  return url.toString();
}

async function fetchReviewsPage(shopDomain, productId, page, rating, sort) {
  const res = await fetch(buildReviewsUrl(shopDomain, productId, page, rating, sort), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Judge.me request failed: ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.reviews) ? json.reviews : [];
}

// Maps the sort dropdown values to Judge.me's sort_by / sort_dir params.
const SORT_OPTIONS = {
  newest: { by: 'created_at', dir: 'desc' },
  oldest: { by: 'created_at', dir: 'asc' },
  highest: { by: 'rating', dir: 'desc' },
  lowest: { by: 'rating', dir: 'asc' },
};

// True when a review has real written content (not blank, not the "[No text]"
// import placeholder). Used to silently suppress empty reviews from the feed.
// Vulgarity filter -----------------------------------------------------------
//
// Hides a review from the storefront when its title or body contains profanity
// or a slur. This is a DISPLAY-SIDE BACKSTOP only — the review still exists in
// Judge.me and should be hidden/archived there properly; nothing here changes
// the review count Judge.me reports.
//
// Deliberately NOT on this list, and it should stay that way:
//   * "hell" — non-negotiable. "A sobering book on heaven and hell" is an
//     ordinary sentence in a review of these publishers' books, so blocking it
//     would hide legitimate reviews constantly. Every other borderline word is
//     rare enough in a customer review to be worth blocking;
//   * minced oaths ("dang", "darn", "heck", "gosh") — nobody balks at those;
//   * "God" and "Jesus" — a reverent use and an exclamatory one are
//     indistinguishable by regex, and on a Christian publisher's storefront the
//     reverent use overwhelmingly dominates. Blocking either would be a
//     catastrophe; "goddamn" is listed instead, which is unambiguous;
//   * insults that are just harsh criticism ("stupid", "idiot", "moron") —
//     unpleasant, but suppressing them is suppressing negative reviews.
// The bar is deliberately strict: a shopper's grandma should be able to read
// any review on the page without wincing. The cost is that a review quoting
// Balaam's ass, the whore of Babylon, or kicking against the pricks is hidden
// along with the vulgar uses — accepted, since those phrasings are vanishingly
// rare in a customer review.
// A merchant who wants any of those blocked can add them per-block via the
// "Additional blocked words" setting rather than editing this list.
const PROFANITY_STEMS = [
  'fuck',
  'motherfuck',
  'clusterfuck',
  'shit',
  'bullshit',
  'horseshit',
  'dogshit',
  'shithead',
  'bitch',
  'cunt',
  'asshole',
  'arsehole',
  'dumbass',
  'smartass',
  'cocksuck',
  'dickhead',
  'pussy',
  'twat',
  'wank',
  'bollock',
  'slut',
  'skank',
  'douche',
  'turd',
  'jizz',
  'blowjob',
  'dildo',
  'goddamn',
  'godamn',
  'faggot',
  'nigger',
  'nigga',
  'kike',
  'wetback',
  'gook',
  'tranny',
  // Crude rather than obscene, but they fail the "would grandma balk" bar.
  'crap',
  'suck',
  'piss',
  'bastard',
  'whore',
  'bugger',
  'porn',
  'wtf',
  'stfu',
  'lmfao',
];

// Stems where the three-letter suffix allowance over-reaches into real words
// ("retardant"), so only exact inflections count. Kept out of the loose list
// rather than dropped, because they are still slurs worth blocking.
// Each of these would collide in the loose tier: retard->"retardant",
// arse->"arsenal", fart->"farther", prick->"prickly", ass->"assure"/"assist".
// Exact inflections only. "damn" sits here for a second reason — it keeps
// "damnation" and "damnable" (5+ trailing letters) clean, which is exactly the
// line this publisher needs: the oath is blocked, the doctrine is not.
const PROFANITY_STRICT_STEMS = [
  'retard',
  'fag',
  'ass',
  'damn',
  'prick',
  'arse',
  'fart',
  'boob',
];

// Common character substitutions, so "sh1t" / "a$$hole" / "f@ggot" still match.
const LEET_MAP = {
  '@': 'a',
  '4': 'a',
  '8': 'b',
  '3': 'e',
  '1': 'i',
  '!': 'i',
  '|': 'i',
  '0': 'o',
  $: 's',
  '5': 's',
  '7': 't',
  '+': 't',
};

// Strips markup and accents, folds leet substitutions, collapses padded repeats
// ("fuuuuck" -> "fuck") and reduces every run of punctuation to one space, so
// matching runs against plain lowercase words.
function normalizeForProfanity(value) {
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[@48310!|$57+]/g, (char) => LEET_MAP[char] || char)
    .replace(/(.)\1{2,}/g, '$1')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(?:[a-z] ){2,}[a-z]\b/g, (run) => run.replace(/ /g, ''))
    .trim();
}

// One regex per block. Stems match at a word boundary plus up to three trailing
// letters, which covers ordinary inflections ("-s", "-ed", "-ing", "-er")
// without reaching into longer unrelated words — the leading \b is what keeps
// "classic", "assassin" and "Scunthorpe" out of it.
function buildProfanityMatcher(enabled, extraWords) {
  if (!enabled) return null;
  const extra = String(extraWords || '')
    .split(',')
    .map((word) => normalizeForProfanity(word))
    .filter(Boolean);
  const escape = (stem) => stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const loose = PROFANITY_STEMS.concat(extra).map(escape);
  const strict = PROFANITY_STRICT_STEMS.map(escape);
  return new RegExp(
    `\\b(?:${loose.join('|')})[a-z]{0,3}\\b|\\b(?:${strict.join('|')})(?:s|es|ed|ing)?\\b`
  );
}

function reviewIsClean(review, matcher) {
  if (!matcher) return true;
  const text = normalizeForProfanity(
    `${review.title || ''} ${review.body || review.body_html || ''}`
  );
  return !matcher.test(text);
}

function reviewHasText(review) {
  const raw = review.body_html || review.body || '';
  const text = String(raw).replace(/<[^>]*>/g, '').trim();
  return text.length > 0 && text.toLowerCase() !== PLACEHOLDER_BODY;
}

// The short on-page feed: the first few substantive reviews plus a "See all
// {count} reviews" button that opens the modal. Filtering/sorting live in
// the modal.
function renderPreview(host, data, labels, classes, replyShop, options, openModal) {
  const root = host.querySelector('[data-cr-feed]');
  if (!root) return;
  const reviews = (Array.isArray(data.reviews) ? data.reviews : []).filter(
    (review) => reviewHasText(review) && reviewIsClean(review, options.profanity_matcher)
  );
  const total = Number(data.number_of_reviews || data.total_reviews || reviews.length || 0);
  if (!total) {
    root.innerHTML = '';
    return;
  }
  const visible = reviews.slice(0, options.preview_count);
  const cards = visible.map((r) => renderCard(r, labels, classes, replyShop, options)).join('');
  const seeAll =
    total > visible.length && openModal
      ? `<button type="button" class="customer-reviews__see-all button button-secondary" data-cr-see-all aria-haspopup="dialog">${escapeHtml(
          (labels.see_all || 'See all {count} reviews').replace('{count}', numberFormatter.format(total))
        )}</button>`
      : '';
  root.innerHTML = `${cards}${seeAll}`;
  const btn = root.querySelector('[data-cr-see-all]');
  if (btn && openModal) {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      openModal();
    });
  }
}

// Builds the full review browser inside the modal: rating filter + sort
// selects drive a paginated feed that auto-loads more reviews as the modal
// body is scrolled (IntersectionObserver on a sentinel after the feed).
// Empty (text-less) reviews are silently suppressed so only substantive
// reviews show.
function createReviewBrowser(host, data, labels, classes, replyShop, options) {
  const modal = host.querySelector('[data-cr-modal]');
  const feedRoot = host.querySelector('[data-cr-modal-feed]');
  if (!modal || !feedRoot) return null;

  const scroller = host.querySelector('[data-cr-modal-scroll]');
  const sortSelect = host.querySelector('[data-cr-sort]');
  const sortValueEl = host.querySelector('[data-cr-sort-value]');
  const filterSelect = host.querySelector('[data-cr-filter]');
  const filterValueEl = host.querySelector('[data-cr-filter-value]');
  const sentinel = host.querySelector('[data-cr-sentinel]');
  const statusEl = host.querySelector('[data-cr-feed-status]');
  const emptyEl = host.querySelector('[data-cr-feed-empty]');
  const errorEl = host.querySelector('[data-cr-feed-error]');
  const retryBtn = host.querySelector('[data-cr-feed-retry]');

  const pagination = data.pagination || {};
  const perPage = Math.max(1, Number(pagination.per_page) || 5);
  const grandTotal = Number(data.number_of_reviews || data.total_reviews || (data.reviews || []).length || 0);
  const sourceTotalPages = Number(pagination.total_pages) || Math.max(1, Math.ceil(grandTotal / perPage));
  const canFetch = Boolean(options.product_id && options.shop_domain);
  const inlineFirstPage = Array.isArray(data.reviews) ? data.reviews.slice() : [];
  const BATCH_SIZE = 10; // cards revealed per scroll-triggered load
  const FETCH_CAP_PER_REVEAL = 8; // bound request bursts (rating filter + empty suppression may skip many)

  let state = { rating: null, sort: 'newest', loaded: [], seen: new Set(), sourcePage: 0, shown: 0, exhausted: false };
  let busy = false;
  // Bumped on every reset; in-flight fetches from a previous filter/sort
  // check it after each await and drop their results instead of mixing
  // stale reviews into the new view.
  let generation = 0;

  // A review is kept if it matches the active rating filter, has real text,
  // and is free of profanity.
  const keep = (r) =>
    (state.rating == null || Number(r.rating) === state.rating) &&
    reviewHasText(r) &&
    reviewIsClean(r, options.profanity_matcher);

  const hasMore = () => state.shown < state.loaded.length || (canFetch && !state.exhausted);

  const setHidden = (el, hidden) => {
    if (el) el.hidden = hidden;
  };

  // Mirror the selected option's label into the visible value text (the native
  // <select> is an invisible overlay, matching the collection sort control).
  const syncSelectValue = (select, valueEl) => {
    if (!select || !valueEl) return;
    const opt = select.options[select.selectedIndex];
    if (opt) valueEl.textContent = opt.textContent.trim();
  };

  const ensureLoaded = async (target, gen) => {
    let fetches = 0;
    while (
      gen === generation &&
      state.loaded.length < target &&
      !state.exhausted &&
      canFetch &&
      fetches < FETCH_CAP_PER_REVEAL
    ) {
      const nextPage = state.sourcePage + 1;
      const fetched = await fetchReviewsPage(
        options.shop_domain,
        options.product_id,
        nextPage,
        state.rating,
        SORT_OPTIONS[state.sort]
      );
      if (gen !== generation) return;
      state.sourcePage = nextPage;
      fetches += 1;
      if (fetched.length < perPage || nextPage >= sourceTotalPages) state.exhausted = true;
      for (const r of fetched) {
        if (r.uuid && state.seen.has(r.uuid)) continue;
        if (r.uuid) state.seen.add(r.uuid);
        if (keep(r)) state.loaded.push(r);
      }
    }
  };

  const appendCards = (items) => {
    if (!items.length) return;
    const buffer = document.createElement('div');
    buffer.innerHTML = items.map((r) => renderCard(r, labels, classes, replyShop, options)).join('');
    while (buffer.firstElementChild) feedRoot.appendChild(buffer.firstElementChild);
  };

  const renderTail = () => {
    const isEmpty = state.shown === 0 && state.loaded.length === 0 && (state.exhausted || !canFetch);
    setHidden(emptyEl, !isEmpty);
    if (isEmpty && emptyEl) {
      emptyEl.textContent = state.rating == null ? labels.empty : labels.filter_empty;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) revealNext();
    },
    { root: scroller || null, rootMargin: '320px 0px' }
  );

  // Re-observing delivers a fresh intersection record, so a sentinel that is
  // STILL visible after a reveal (short batch) immediately triggers the next
  // one until the modal body is filled or the source is exhausted.
  const rearmObserver = () => {
    if (!sentinel) return;
    observer.unobserve(sentinel);
    if (hasMore()) observer.observe(sentinel);
  };

  const revealNext = async () => {
    if (busy) return;
    busy = true;
    const gen = generation;
    setHidden(errorEl, true);
    setHidden(retryBtn, true);
    const target = state.shown + BATCH_SIZE;

    if (state.loaded.length < target && canFetch && !state.exhausted) {
      setHidden(statusEl, false);
      try {
        await ensureLoaded(target, gen);
      } catch (e) {
        busy = false;
        if (gen !== generation) {
          revealNext();
          return;
        }
        setHidden(statusEl, true);
        setHidden(errorEl, false);
        setHidden(retryBtn, false);
        return;
      }
      setHidden(statusEl, true);
    }

    if (gen !== generation) {
      busy = false;
      revealNext();
      return;
    }

    const slice = state.loaded.slice(state.shown, target);
    appendCards(slice);
    state.shown += slice.length;
    renderTail();
    busy = false;
    rearmObserver();
  };

  const reset = (rating, sortKey) => {
    generation += 1;
    feedRoot.innerHTML = '';
    setHidden(emptyEl, true);
    setHidden(errorEl, true);
    setHidden(retryBtn, true);
    setHidden(statusEl, true);
    state = {
      rating: rating == null ? null : Number(rating),
      sort: sortKey || 'newest',
      loaded: [],
      seen: new Set(),
      sourcePage: 0,
      shown: 0,
      exhausted: false,
    };
    // Reuse Judge.me's inlined first page only when it matches the active view
    // (all ratings, newest first); otherwise fetch fresh from the server.
    if (state.rating == null && state.sort === 'newest' && inlineFirstPage.length) {
      for (const r of inlineFirstPage) {
        if (r.uuid) state.seen.add(r.uuid);
        if (keep(r)) state.loaded.push(r);
      }
      state.sourcePage = Number(pagination.current_page) || 1;
      if (state.sourcePage >= sourceTotalPages) state.exhausted = true;
    }
    if (filterSelect) filterSelect.value = state.rating == null ? 'all' : String(state.rating);
    if (sortSelect) sortSelect.value = state.sort;
    syncSelectValue(filterSelect, filterValueEl);
    syncSelectValue(sortSelect, sortValueEl);
    if (scroller) scroller.scrollTop = 0;
    revealNext();
  };

  if (filterSelect) {
    filterSelect.addEventListener('change', () => {
      const value = filterSelect.value;
      reset(value === 'all' ? null : Number(value), state.sort);
    });
  }
  if (sortSelect) {
    // reset() re-syncs both native-control value spans (syncSelectValue).
    sortSelect.addEventListener('change', () => reset(state.rating, sortSelect.value));
  }
  if (retryBtn) {
    retryBtn.addEventListener('click', (event) => {
      event.preventDefault();
      revealNext();
    });
  }

  reset(null, sortSelect ? sortSelect.value : 'newest');

  return {
    openModal() {
      window.publishStorefrontEvent?.('reviews_opened', { placement: 'product_page' });
      if (typeof modal.showDialog === 'function') modal.showDialog();
    },
    filterAndOpen(rating) {
      if (state.rating !== rating) reset(rating, state.sort);
      this.openModal();
    },
  };
}

function wireWriteButton(host) {
  const btn = host.querySelector('[data-cr-write-trigger]');
  if (!btn) return;
  btn.addEventListener('click', () => {
    window.publishStorefrontEvent?.('review_write_clicked', { placement: 'product_page' });
    const trigger = findJudgemeWriteTrigger(host);
    if (trigger) trigger.click();
  });
}

// Store-wide aggregate (all products) is exposed on Judge.me's widget container
// as data-shop-average-rating / data-shop-review-count — not in the inlined
// product data object — so read it from the DOM.
function getStoreStats() {
  const el = document.querySelector('[data-shop-average-rating][data-shop-review-count]');
  if (!el) return null;
  const avg = parseFloat(el.getAttribute('data-shop-average-rating'));
  const count = parseInt(el.getAttribute('data-shop-review-count'), 10);
  if (!Number.isFinite(avg) || !Number.isFinite(count) || count <= 0) return null;
  return { avg, count };
}

function waitForStoreStats(maxMs = 6000) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const stats = getStoreStats();
      if (stats) return resolve(stats);
      if (Date.now() - start > maxMs) return resolve(null);
      setTimeout(tick, 200);
    };
    tick();
  });
}

async function renderStoreRating(host, labels) {
  const root = host.querySelector('[data-cr-store-rating]');
  if (!root) return; // indicator disabled in settings
  const stats = await waitForStoreStats();
  if (!stats) return;
  const countText = pluralize(stats.count, labels.store_reviews_one, labels.store_reviews_other);
  root.innerHTML =
    `<span class="cr-store-rating__stars" aria-hidden="true">${fractionalStars(stats.avg, 'cr-star--sm')}</span>` +
    `<span class="cr-store-rating__text">${escapeHtml(stats.avg.toFixed(2))} · ${escapeHtml(countText)}</span>`;
}

// Deep entry states: the modal markup renders a "Loading…" row before the
// widget data resolves; settle it into the terminal empty/error message.
function setModalMessage(host, kind) {
  const status = host.querySelector('[data-cr-feed-status]');
  if (status) status.hidden = true;
  const el = host.querySelector(kind === 'error' ? '[data-cr-feed-error]' : '[data-cr-feed-empty]');
  if (el) el.hidden = false;
}

async function hydrate(host) {
  const productId = host.dataset.productId;

  const labels = {
    out_of: host.dataset.labelOutOf || 'out of 5.0',
    reviews_one: host.dataset.labelReviewsOne || '{count} review',
    reviews_other: host.dataset.labelReviewsOther || '{count} reviews',
    stars_row: host.dataset.labelStarsRow || '{count} stars',
    verified: host.dataset.labelVerified || 'Verified',
    zoom: host.dataset.labelZoom || 'Zoom',
    anonymous: host.dataset.labelAnonymous || 'Anonymous',
    see_all: host.dataset.labelSeeAll || 'See all {count} reviews',
    empty: host.dataset.labelEmpty || 'No reviews yet.',
    filter_empty: host.dataset.labelFilterEmpty || 'No reviews with this rating.',
    error: host.dataset.labelError || 'Reviews could not be loaded.',
    store_reviews_one: host.dataset.labelStoreReviewsOne || '{count} store review',
    store_reviews_other: host.dataset.labelStoreReviewsOther || '{count} store reviews',
  };
  const classes = {
    rating: host.dataset.ratingClass || '',
    name: host.dataset.nameClass || '',
    title: host.dataset.titleClass || '',
    body: host.dataset.bodyClass || '',
  };
  const replyShop = {
    name: host.dataset.shopReplyName || '',
    logo: host.dataset.shopReplyLogo || '',
  };
  const options = {
    preview_count: Number(host.dataset.previewCount) || 5,
    avatar_fallback: host.dataset.avatarFallback || 'icon',
    product_id: host.dataset.productId || '',
    shop_domain: host.dataset.shopDomain || '',
    // Absent dataset (a template saved before this setting existed) leaves the
    // filter ON — only an explicit "false" turns it off.
    profanity_matcher: buildProfanityMatcher(
      host.dataset.profanityFilter !== 'false',
      host.dataset.profanityWords
    ),
  };

  wireWriteButton(host);
  wireLightbox(host, labels);

  if (!productId) {
    host.setAttribute('data-state', 'error');
    return;
  }

  host.setAttribute('data-state', 'loading');

  const result = await waitForWidgetData(productId);

  if (result.status === 'error') {
    host.setAttribute('data-state', 'error');
    const summary = host.querySelector('[data-cr-summary]');
    if (summary)
      summary.innerHTML = `<p class="customer-reviews__error">${escapeHtml(labels.error)}</p>`;
    setModalMessage(host, 'error');
    return;
  }

  if (result.status === 'empty') {
    host.setAttribute('data-state', 'empty');
    renderSummary(host, { average_rating: 0, total_reviews: 0, histogram: [] }, labels, classes);
    setModalMessage(host, 'empty');
    renderStoreRating(host, labels);
    return;
  }

  host.setAttribute('data-state', 'loaded');
  renderSummary(host, result.data, labels, classes);
  const browser = createReviewBrowser(host, result.data, labels, classes, replyShop, options);
  const openModal = browser ? () => browser.openModal() : null;
  renderPreview(host, result.data, labels, classes, replyShop, options, openModal);

  const summaryEl = host.querySelector('[data-cr-summary]');
  if (summaryEl && browser) {
    summaryEl.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cr-rating]');
      if (!btn) return;
      event.preventDefault();
      browser.filterAndOpen(Number(btn.dataset.crRating));
    });
  }

  renderStoreRating(host, labels);
}

function init() {
  document.querySelectorAll('[data-customer-reviews]').forEach(hydrate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
